
import Vue from 'vue';

import * as CorpusStore from '@/store/search/corpus';
import * as InterfaceStore from '@/store/search/form/interface';
import * as ExploreStore from '@/store/search/form/explore';
import * as UIStore from '@/store/search/ui';

import SelectPicker, {Option, OptGroup} from '@/components/SelectPicker.vue';
import Autocomplete from '@/components/Autocomplete.vue';
import Lexicon from '@/pages/search/form/Lexicon.vue';
import { getAnnotationSubset, getMetadataSubset } from '@/utils';
import { blacklabPaths } from '@/api';

import debug from '@/utils/debug';

export default Vue.extend({
	components: {
		SelectPicker,
		Autocomplete,
		Lexicon
	},
	data: () => ({
		debug
	}),
	computed: {
		exploreMode: {
			get(): string { return InterfaceStore.getState().exploreMode; },
			set: InterfaceStore.actions.exploreMode,
		},

		ngramSize: {
			get: ExploreStore.get.ngram.size,
			set: ExploreStore.actions.ngram.size,
		},

		ngramType: {
			get: ExploreStore.get.ngram.groupAnnotationId,
			set: ExploreStore.actions.ngram.groupAnnotationId,
		},

		ngramTokens() {
			const allAnnotations = CorpusStore.get.allAnnotationsMap();
			return ExploreStore.get.ngram.tokens().map(tok => ({
				...tok,
				annotation: allAnnotations[tok.id]
			}));
		},
		ngramSizeMax: ExploreStore.get.ngram.maxSize,

		frequencyType: {
			get: ExploreStore.get.frequency.annotationId,
			set: ExploreStore.actions.frequency.annotationId,
		},

		corporaGroupBy: {
			get: ExploreStore.get.corpora.groupBy,
			set: ExploreStore.actions.corpora.groupBy,
		},
		corporaGroupDisplayMode: {
			get: ExploreStore.get.corpora.groupDisplayMode,
			set: ExploreStore.actions.corpora.groupDisplayMode,
		},

		annotationSearchOptions(): Option[]|OptGroup[] {
			const optGroups = getAnnotationSubset(
				UIStore.getState().explore.searchAnnotationIds,
				CorpusStore.get.annotationGroups(),
				CorpusStore.get.allAnnotationsMap(),
				'Search',
				this,
				CorpusStore.get.textDirection(),
				debug.debug,
				false
			);
			return optGroups.length > 1 ? optGroups : optGroups.flatMap(g => g.options as Option[]);
		},
		annotationGroupByOptions(): Option[]|OptGroup[] {
			const optGroups = getAnnotationSubset(
				UIStore.getState().results.shared.groupAnnotationIds,
				CorpusStore.get.annotationGroups(),
				CorpusStore.get.allAnnotationsMap(),
				'Search', // we don't want the before hit/after hit context options, just do search mode, it'll be fine
				this,
				CorpusStore.get.textDirection(),
				debug.debug,
				UIStore.getState().dropdowns.groupBy.annotationGroupLabelsVisible
			);
			return optGroups.length > 1 ? optGroups : optGroups.flatMap(g => g.options as Option[]);
		},
		metadataGroupByOptions(): OptGroup[] {
			// we removed the field:prefix from metadata grouping options
			// since the new groupby window. so we need to fix this here
			function fix(o: OptGroup|Option) {
				if ('value' in o) {o.value = 'field:'+o.value;}
				else o.options.forEach(opt => {if (!(typeof opt === 'string')) fix(opt);});
			}

			const optGroups = getMetadataSubset(
				UIStore.getState().results.shared.groupMetadataIds,
				CorpusStore.get.metadataGroups(),
				CorpusStore.get.allMetadataFieldsMap(),
				'Group',
				this,
				debug.debug,
				UIStore.getState().dropdowns.groupBy.metadataGroupLabelsVisible
			);
			optGroups.forEach(fix);
			return optGroups;
		},
		corporaGroupDisplayModeOptions(): Option[] {
			// TODO
			return [{
				value: 'table',
				label: this.$t('explore.corpora.showAs.table').toString(),
			}, {
				value: 'docs',
				label: this.$t('explore.corpora.showAs.docs').toString(),
			}, {
				value: 'tokens',
				label: this.$t('explore.corpora.showAs.tokens').toString(),
			}];
		},

		mainTokenTextDirection: CorpusStore.get.textDirection,
	},
	methods: {
		updateTokenAnnotation(index: number, id: string) {
			ExploreStore.actions.ngram.token({
				index,
				token: { id }
			});
		},
		updateTokenValue(index: number, value: string) {
			ExploreStore.actions.ngram.token({
				index,
				token: { value }
			});
		},
		autocompleteUrl(annot: CorpusStore.NormalizedAnnotation) {
			return blacklabPaths.autocompleteAnnotation(INDEX_ID, annot.annotatedFieldId, annot.id);
		}
	},
	created() {
		this.corporaGroupDisplayMode = this.corporaGroupDisplayModeOptions[0].value;
	}
});
